import { useEffect, useCallback } from 'react'
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp'

interface TelegramClosingConfirmationProps {
  enabled?: boolean
  message?: string
  onConfirm?: () => void
  onCancel?: () => void
}

export function TelegramClosingConfirmation({
  enabled = true,
  message = 'Are you sure you want to leave?',
  onConfirm,
  onCancel
}: TelegramClosingConfirmationProps) {
  const { webApp, showConfirm } = useTelegramWebApp()

  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    if (enabled) {
      event.preventDefault()
      event.returnValue = message
      return message
    }
  }, [enabled, message])

  const handleTelegramClose = useCallback(async () => {
    if (enabled) {
      try {
        const confirmed = await showConfirm(message)
        if (confirmed) {
          onConfirm?.()
          webApp?.close()
        } else {
          onCancel?.()
        }
      } catch (error) {
        console.error('Error showing confirmation:', error)
        onCancel?.()
      }
    } else {
      webApp?.close()
    }
  }, [enabled, message, showConfirm, webApp, onConfirm, onCancel])

  useEffect(() => {
    if (!webApp) return

    // Enable closing confirmation in Telegram
    if (enabled) {
      webApp.enableClosingConfirmation()

      // Listen for the mainButtonClicked event which can be used for close confirmation
      const handleMainButtonClick = () => {
        handleTelegramClose()
      }

      // We can't directly override the close behavior, but we can at least show confirmation
      // when the user tries to navigate away
      webApp.onEvent('mainButtonClicked', handleMainButtonClick)

      return () => {
        webApp.offEvent('mainButtonClicked', handleMainButtonClick)
        webApp.disableClosingConfirmation()
      }
    } else {
      webApp.disableClosingConfirmation()
    }
  }, [enabled, webApp, handleTelegramClose])

  useEffect(() => {
    // Handle browser close/refresh
    if (enabled) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
    }
  }, [enabled, handleBeforeUnload])

  // This component doesn't render anything visible
  return null
}

// Hook for easier usage
export function useTelegramClosingConfirmation(
  enabled: boolean = true,
  message: string = 'Are you sure you want to leave?'
) {
  const { webApp, showConfirm } = useTelegramWebApp()

  const enableClosingConfirmation = useCallback(() => {
    webApp?.enableClosingConfirmation()
  }, [webApp])

  const disableClosingConfirmation = useCallback(() => {
    webApp?.disableClosingConfirmation()
  }, [webApp])

  const showConfirmationDialog = useCallback(async (): Promise<boolean> => {
    try {
      return await showConfirm(message)
    } catch (error) {
      console.error('Error showing confirmation:', error)
      return false
    }
  }, [showConfirm, message])

  useEffect(() => {
    if (enabled) {
      enableClosingConfirmation()
    } else {
      disableClosingConfirmation()
    }

    return () => {
      disableClosingConfirmation()
    }
  }, [enabled, enableClosingConfirmation, disableClosingConfirmation])

  return {
    enableClosingConfirmation,
    disableClosingConfirmation,
    showConfirmationDialog,
    isEnabled: enabled
  }
}